// ===== Supabase Client =====
// غيّر الـ URL والـ Key حسب مشروعك في Supabase

import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = "https://fnuoarrumiyewwxrfcwp.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZudW9hcnJ1bWl5ZXd3eHJmY3dwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzkxNjk1ODIsImV4cCI6MjA5NDc0NTU4Mn0.Vx0sO5ypwvYDsKuICE7owAlfu311aPGXyvW0reK7baA";

// ===== Helper: Fetch from Supabase REST API =====
async function supaFetch(table, { method = "GET", body, query = "", headers = {} } = {}) {
  const url = `${SUPABASE_URL}/rest/v1/${table}${query}`;
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token || SUPABASE_ANON_KEY;
  const opts = {
    method,
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
      Prefer: method === "POST" ? "return=representation" : method === "PATCH" ? "return=representation" : undefined,
      ...headers,
    },
  };
  if (body) opts.body = JSON.stringify(body);
  // Clean undefined headers
  Object.keys(opts.headers).forEach((k) => {
    if (opts.headers[k] === undefined) delete opts.headers[k];
  });
  const res = await fetch(url, opts);
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Supabase error (${res.status}): ${err}`);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

// ===== CRUD Functions =====

export async function supaGet(table, query = "") {
  return supaFetch(table, { query: query ? `?${query}` : "" });
}

export async function supaInsert(table, data) {
  return supaFetch(table, { method: "POST", body: data });
}

export async function supaUpdate(table, query, data) {
  return supaFetch(table, { method: "PATCH", body: data, query: `?${query}` });
}

export async function supaDelete(table, query) {
  return supaFetch(table, { method: "DELETE", query: `?${query}` });
}

// ===== User & Notification Helpers =====

export async function getNotifications(userId) {
  return supaGet("notifications", `user_id=eq.${userId}&order=created_at.desc&limit=20`);
}

export async function markNotificationRead(id) {
  return supaUpdate("notifications", `id=eq.${id}`, { is_read: true });
}

export async function createNotification(data) {
  return supaInsert("notifications", data);
}

export async function getUnreadCount(userId) {
  const rows = await supaGet("notifications", `user_id=eq.${userId}&is_read=eq.false&select=id`);
  return Array.isArray(rows) ? rows.length : 0;
}

export async function getAllUsers() {
  return supaGet("users", "select=*&order=created_at.desc");
}

export async function toggleUserStatus(userId, isActive) {
  return supaUpdate("users", `id=eq.${userId}`, { is_active: isActive });
}

export async function addAuditLog(userId, username, action, details) {
  return supaInsert("audit_log", {
    user_id: userId,
    username,
    action,
    details,
  });
}

export async function addNotification(userId, title, message, type = "info") {
  return supaInsert("notifications", {
    user_id: userId,
    title,
    message,
    type,
    is_read: false,
  });
}

// ===== Requests (Self-Service) =====

export async function createRequest(payload) {
  const rows = await supaInsert("requests", { ...payload, status: "new" });
  return Array.isArray(rows) ? rows[0] : rows;
}

export async function getRequests(query = "") {
  return supaGet("requests", query);
}

export async function advanceRequest(id, { stage, actorId, actorName, decision, note, nextStatus }) {
  await supaInsert("request_approvals", {
    request_id: id,
    stage,
    actor_id: actorId,
    actor_name: actorName,
    decision,
    note: note || null,
  });
  return supaUpdate("requests", `id=eq.${id}`, { status: nextStatus });
}

export async function getRequestTrail(requestId) {
  return supaGet("request_approvals", `request_id=eq.${requestId}&order=created_at.asc`);
}

// ===== Phase 12b additions =====

// ---- Generic helpers (used by all module-specific CRUDs below) ----
function _phase12bCurrentUser() {
  try {
    const raw =
      typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem("currentUser")
        : null;
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
}

// المستخدم الحالي الموحّد (يُقرأ من localStorage 'currentUser').
// غلاف مُصدَّر حول _phase12bCurrentUser ليُستهلك من المكوّنات.
// يرجّع كائن المستخدم (فيه id/role/...) أو null.
export function getCurrentUser() {
  return _phase12bCurrentUser();
}

function _phase12bBuildFilter(filter) {
  if (!filter) return "";
  return Object.entries(filter)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=eq.${encodeURIComponent(v)}`)
    .join("&");
}

export async function supaList(
  table,
  { filter = {}, orderBy = "created_at", ascending = false, limit = 500 } = {}
) {
  try {
    const parts = ["select=*"];
    const f = _phase12bBuildFilter(filter);
    if (f) parts.push(f);
    parts.push(
      `order=${encodeURIComponent(orderBy)}.${ascending ? "asc" : "desc"}`
    );
    parts.push(`limit=${limit}`);
    const data = await supaFetch(table, { query: `?${parts.join("&")}` });
    return {
      data: Array.isArray(data) ? data : data ? [data] : [],
      error: null,
    };
  } catch (error) {
    return { data: null, error: error?.message || String(error) };
  }
}

export async function supaGetById(table, id) {
  try {
    const rows = await supaFetch(table, {
      query: `?id=eq.${encodeURIComponent(id)}&select=*&limit=1`,
    });
    const row = Array.isArray(rows) ? rows[0] || null : rows;
    return { data: row, error: null };
  } catch (error) {
    return { data: null, error: error?.message || String(error) };
  }
}

export async function supaCreate(table, row) {
  try {
    const user = _phase12bCurrentUser();
    const payload = {
      ...row,
      created_at: row?.created_at || new Date().toISOString(),
      created_by: row?.created_by || user?.id || user?.email || null,
    };
    const rows = await supaFetch(table, { method: "POST", body: payload });
    const created = Array.isArray(rows) ? rows[0] || null : rows;
    return { data: created, error: null };
  } catch (error) {
    return { data: null, error: error?.message || String(error) };
  }
}

export async function supaPatch(table, id, patch) {
  try {
    const user = _phase12bCurrentUser();
    const payload = {
      ...patch,
      updated_at: new Date().toISOString(),
      updated_by: user?.id || user?.email || null,
    };
    const rows = await supaFetch(table, {
      method: "PATCH",
      body: payload,
      query: `?id=eq.${encodeURIComponent(id)}`,
    });
    const updated = Array.isArray(rows) ? rows[0] || null : rows;
    return { data: updated, error: null };
  } catch (error) {
    return { data: null, error: error?.message || String(error) };
  }
}

export async function supaRemove(table, id) {
  try {
    await supaFetch(table, {
      method: "DELETE",
      query: `?id=eq.${encodeURIComponent(id)}`,
    });
    return { data: { id }, error: null };
  } catch (error) {
    return { data: null, error: error?.message || String(error) };
  }
}

// ----- EDawah -----
export const getEDawahPreachers = (filter) => supaList("edawah_preachers", { filter });
export const createEDawahPreacher = (row) => supaCreate("edawah_preachers", row);
export const updateEDawahPreacher = (id, patch) => supaPatch("edawah_preachers", id, patch);
export const deleteEDawahPreacher = (id) => supaRemove("edawah_preachers", id);

export const getEDawahConversations = (filter) => supaList("edawah_conversations", { filter });
export const createEDawahConversation = (row) => supaCreate("edawah_conversations", row);
export const updateEDawahConversation = (id, patch) => supaPatch("edawah_conversations", id, patch);
export const deleteEDawahConversation = (id) => supaRemove("edawah_conversations", id);

export const getEDawahNewMuslims = (filter) => supaList("edawah_new_muslims", { filter });
export const createEDawahNewMuslim = (row) => supaCreate("edawah_new_muslims", row);
export const updateEDawahNewMuslim = (id, patch) => supaPatch("edawah_new_muslims", id, patch);
export const deleteEDawahNewMuslim = (id) => supaRemove("edawah_new_muslims", id);

export const getEDawahEducation = (filter) => supaList("edawah_education", { filter });
export const createEDawahEducation = (row) => supaCreate("edawah_education", row);
export const updateEDawahEducation = (id, patch) => supaPatch("edawah_education", id, patch);
export const deleteEDawahEducation = (id) => supaRemove("edawah_education", id);

export const getEDawahRetention = (filter) => supaList("edawah_retention", { filter });
export const createEDawahRetention = (row) => supaCreate("edawah_retention", row);
export const updateEDawahRetention = (id, patch) => supaPatch("edawah_retention", id, patch);
export const deleteEDawahRetention = (id) => supaRemove("edawah_retention", id);

export const getEDawahRatings = (filter) => supaList("edawah_ratings", { filter });
export const createEDawahRating = (row) => supaCreate("edawah_ratings", row);
export const updateEDawahRating = (id, patch) => supaPatch("edawah_ratings", id, patch);
export const deleteEDawahRating = (id) => supaRemove("edawah_ratings", id);

export const getEDawahArchive = (filter) => supaList("edawah_archive", { filter });
export const createEDawahArchive = (row) => supaCreate("edawah_archive", row);
export const updateEDawahArchive = (id, patch) => supaPatch("edawah_archive", id, patch);
export const deleteEDawahArchive = (id) => supaRemove("edawah_archive", id);

// ----- Institute -----
export const getInstituteStudents = (filter) => supaList("institute_students", { filter });
export const createInstituteStudent = (row) => supaCreate("institute_students", row);
export const updateInstituteStudent = (id, patch) => supaPatch("institute_students", id, patch);
export const deleteInstituteStudent = (id) => supaRemove("institute_students", id);

export const getInstituteCourses = (filter) => supaList("institute_courses", { filter });
export const createInstituteCourse = (row) => supaCreate("institute_courses", row);
export const updateInstituteCourse = (id, patch) => supaPatch("institute_courses", id, patch);
export const deleteInstituteCourse = (id) => supaRemove("institute_courses", id);

export const getInstituteExams = (filter) => supaList("institute_exams", { filter });
export const createInstituteExam = (row) => supaCreate("institute_exams", row);
export const updateInstituteExam = (id, patch) => supaPatch("institute_exams", id, patch);
export const deleteInstituteExam = (id) => supaRemove("institute_exams", id);

export const getInstituteAttendance = (filter) => supaList("institute_attendance", { filter });
export const createInstituteAttendance = (row) => supaCreate("institute_attendance", row);
export const updateInstituteAttendance = (id, patch) => supaPatch("institute_attendance", id, patch);
export const deleteInstituteAttendance = (id) => supaRemove("institute_attendance", id);

export const getInstituteCertificates = (filter) => supaList("institute_certificates", { filter });
export const createInstituteCertificate = (row) => supaCreate("institute_certificates", row);
export const updateInstituteCertificate = (id, patch) => supaPatch("institute_certificates", id, patch);
export const deleteInstituteCertificate = (id) => supaRemove("institute_certificates", id);

export const getInstituteStaff = (filter) => supaList("institute_staff", { filter });
export const createInstituteStaffMember = (row) => supaCreate("institute_staff", row);
export const updateInstituteStaffMember = (id, patch) => supaPatch("institute_staff", id, patch);
export const deleteInstituteStaffMember = (id) => supaRemove("institute_staff", id);

// ----- Assets -----
export const getAssetsAssets = (filter) => supaList("assets_assets", { filter });
export const createAssetsAsset = (row) => supaCreate("assets_assets", row);
export const updateAssetsAsset = (id, patch) => supaPatch("assets_assets", id, patch);
export const deleteAssetsAsset = (id) => supaRemove("assets_assets", id);

export const getAssetsCustody = (filter) => supaList("assets_custody", { filter });
export const createAssetsCustody = (row) => supaCreate("assets_custody", row);
export const updateAssetsCustody = (id, patch) => supaPatch("assets_custody", id, patch);
export const deleteAssetsCustody = (id) => supaRemove("assets_custody", id);

export const getAssetsSims = (filter) => supaList("assets_sims", { filter });
export const createAssetsSim = (row) => supaCreate("assets_sims", row);
export const updateAssetsSim = (id, patch) => supaPatch("assets_sims", id, patch);
export const deleteAssetsSim = (id) => supaRemove("assets_sims", id);

export const getAssetsVehicles = (filter) => supaList("assets_vehicles", { filter });
export const createAssetsVehicle = (row) => supaCreate("assets_vehicles", row);
export const updateAssetsVehicle = (id, patch) => supaPatch("assets_vehicles", id, patch);
export const deleteAssetsVehicle = (id) => supaRemove("assets_vehicles", id);

export const getAssetsMaintenance = (filter) => supaList("assets_maintenance", { filter });
export const createAssetsMaintenance = (row) => supaCreate("assets_maintenance", row);
export const updateAssetsMaintenance = (id, patch) => supaPatch("assets_maintenance", id, patch);
export const deleteAssetsMaintenance = (id) => supaRemove("assets_maintenance", id);

export const getAssetsInventory = (filter) => supaList("assets_inventory", { filter });
export const createAssetsInventory = (row) => supaCreate("assets_inventory", row);
export const updateAssetsInventory = (id, patch) => supaPatch("assets_inventory", id, patch);
export const deleteAssetsInventory = (id) => supaRemove("assets_inventory", id);

export const getAssetsDocuments = (filter) => supaList("assets_documents", { filter });
export const createAssetsDocument = (row) => supaCreate("assets_documents", row);
export const updateAssetsDocument = (id, patch) => supaPatch("assets_documents", id, patch);
export const deleteAssetsDocument = (id) => supaRemove("assets_documents", id);

// ----- Waqf -----
export const getWaqfProperties = (filter) => supaList("waqf_properties", { filter });
export const createWaqfProperty = (row) => supaCreate("waqf_properties", row);
export const updateWaqfProperty = (id, patch) => supaPatch("waqf_properties", id, patch);
export const deleteWaqfProperty = (id) => supaRemove("waqf_properties", id);

export const getWaqfUnits = (filter) => supaList("waqf_units", { filter });
export const createWaqfUnit = (row) => supaCreate("waqf_units", row);
export const updateWaqfUnit = (id, patch) => supaPatch("waqf_units", id, patch);
export const deleteWaqfUnit = (id) => supaRemove("waqf_units", id);

export const getWaqfTenants = (filter) => supaList("waqf_tenants", { filter });
export const createWaqfTenant = (row) => supaCreate("waqf_tenants", row);
export const updateWaqfTenant = (id, patch) => supaPatch("waqf_tenants", id, patch);
export const deleteWaqfTenant = (id) => supaRemove("waqf_tenants", id);

export const getWaqfContracts = (filter) => supaList("waqf_contracts", { filter });
export const createWaqfContract = (row) => supaCreate("waqf_contracts", row);
export const updateWaqfContract = (id, patch) => supaPatch("waqf_contracts", id, patch);
export const deleteWaqfContract = (id) => supaRemove("waqf_contracts", id);

export const getWaqfPayments = (filter) => supaList("waqf_payments", { filter });
export const createWaqfPayment = (row) => supaCreate("waqf_payments", row);
export const updateWaqfPayment = (id, patch) => supaPatch("waqf_payments", id, patch);
export const deleteWaqfPayment = (id) => supaRemove("waqf_payments", id);

export const getWaqfCorrespondence = (filter) => supaList("waqf_correspondence", { filter });
export const createWaqfCorrespondence = (row) => supaCreate("waqf_correspondence", row);
export const updateWaqfCorrespondence = (id, patch) => supaPatch("waqf_correspondence", id, patch);
export const deleteWaqfCorrespondence = (id) => supaRemove("waqf_correspondence", id);

export const getWaqfDocuments = (filter) => supaList("waqf_documents", { filter });
export const createWaqfDocument = (row) => supaCreate("waqf_documents", row);
export const updateWaqfDocument = (id, patch) => supaPatch("waqf_documents", id, patch);
export const deleteWaqfDocument = (id) => supaRemove("waqf_documents", id);

export const getWaqfFiles = (filter) => supaList("waqf_files", { filter });

// ملف الوقف — جدول منفصل عن waqf_files (الأخير مرفقات عامة).
// يتطلب تنفيذ scripts/waqf-schema-drift-fix.sql أولاً.
export const getWaqfEndowments = (filter) => supaList("waqf_endowments", { filter });
export const createWaqfEndowment = (row) => supaCreate("waqf_endowments", row);
export const updateWaqfEndowment = (id, patch) => supaPatch("waqf_endowments", id, patch);
export const deleteWaqfEndowment = (id) => supaRemove("waqf_endowments", id);
export const createWaqfFile = (row) => supaCreate("waqf_files", row);
export const updateWaqfFile = (id, patch) => supaPatch("waqf_files", id, patch);
export const deleteWaqfFile = (id) => supaRemove("waqf_files", id);

// ----- Camp -----
export const getCampEditions = (filter) => supaList("camp_editions", { filter });
export const createCampEdition = (row) => supaCreate("camp_editions", row);
export const updateCampEdition = (id, patch) => supaPatch("camp_editions", id, patch);
export const deleteCampEdition = (id) => supaRemove("camp_editions", id);

export const getCampLocations = (filter) => supaList("camp_locations", { filter });
export const createCampLocation = (row) => supaCreate("camp_locations", row);
export const updateCampLocation = (id, patch) => supaPatch("camp_locations", id, patch);
export const deleteCampLocation = (id) => supaRemove("camp_locations", id);

export const getCampCommittees = (filter) => supaList("camp_committees", { filter });
export const createCampCommittee = (row) => supaCreate("camp_committees", row);
export const updateCampCommittee = (id, patch) => supaPatch("camp_committees", id, patch);
export const deleteCampCommittee = (id) => supaRemove("camp_committees", id);

export const getCampCommitteeMembers = (filter) => supaList("camp_committee_members", { filter });
export const createCampCommitteeMember = (row) => supaCreate("camp_committee_members", row);
export const updateCampCommitteeMember = (id, patch) => supaPatch("camp_committee_members", id, patch);
export const deleteCampCommitteeMember = (id) => supaRemove("camp_committee_members", id);

export const getCampMeetings = (filter) => supaList("camp_meetings", { filter });
export const createCampMeeting = (row) => supaCreate("camp_meetings", row);
export const updateCampMeeting = (id, patch) => supaPatch("camp_meetings", id, patch);
export const deleteCampMeeting = (id) => supaRemove("camp_meetings", id);

export const getCampTasks = (filter) => supaList("camp_tasks", { filter });
export const createCampTask = (row) => supaCreate("camp_tasks", row);
export const updateCampTask = (id, patch) => supaPatch("camp_tasks", id, patch);
export const deleteCampTask = (id) => supaRemove("camp_tasks", id);

export const getCampVolunteers = (filter) => supaList("camp_volunteers", { filter });
export const createCampVolunteer = (row) => supaCreate("camp_volunteers", row);
export const updateCampVolunteer = (id, patch) => supaPatch("camp_volunteers", id, patch);
export const deleteCampVolunteer = (id) => supaRemove("camp_volunteers", id);

export const getCampProcurement = (filter) => supaList("camp_procurement", { filter });
export const createCampProcurement = (row) => supaCreate("camp_procurement", row);
export const updateCampProcurement = (id, patch) => supaPatch("camp_procurement", id, patch);
export const deleteCampProcurement = (id) => supaRemove("camp_procurement", id);

export const getCampVendors = (filter) => supaList("camp_vendors", { filter });
export const createCampVendor = (row) => supaCreate("camp_vendors", row);
export const updateCampVendor = (id, patch) => supaPatch("camp_vendors", id, patch);
export const deleteCampVendor = (id) => supaRemove("camp_vendors", id);

export const getCampPartners = (filter) => supaList("camp_partners", { filter });
export const createCampPartner = (row) => supaCreate("camp_partners", row);
export const updateCampPartner = (id, patch) => supaPatch("camp_partners", id, patch);
export const deleteCampPartner = (id) => supaRemove("camp_partners", id);

export const getCampLetters = (filter) => supaList("camp_letters", { filter });
export const createCampLetter = (row) => supaCreate("camp_letters", row);
export const updateCampLetter = (id, patch) => supaPatch("camp_letters", id, patch);
export const deleteCampLetter = (id) => supaRemove("camp_letters", id);

export const getCampPermits = (filter) => supaList("camp_permits", { filter });
export const createCampPermit = (row) => supaCreate("camp_permits", row);
export const updateCampPermit = (id, patch) => supaPatch("camp_permits", id, patch);
export const deleteCampPermit = (id) => supaRemove("camp_permits", id);

export const getCampDawahPrograms = (filter) => supaList("camp_dawah_programs", { filter });
export const createCampDawahProgram = (row) => supaCreate("camp_dawah_programs", row);
export const updateCampDawahProgram = (id, patch) => supaPatch("camp_dawah_programs", id, patch);
export const deleteCampDawahProgram = (id) => supaRemove("camp_dawah_programs", id);

export const getCampNewMuslims = (filter) => supaList("camp_new_muslims", { filter });
export const createCampNewMuslim = (row) => supaCreate("camp_new_muslims", row);
export const updateCampNewMuslim = (id, patch) => supaPatch("camp_new_muslims", id, patch);
export const deleteCampNewMuslim = (id) => supaRemove("camp_new_muslims", id);

export const getCampOfficialVisits = (filter) => supaList("camp_official_visits", { filter });
export const createCampOfficialVisit = (row) => supaCreate("camp_official_visits", row);
export const updateCampOfficialVisit = (id, patch) => supaPatch("camp_official_visits", id, patch);
export const deleteCampOfficialVisit = (id) => supaRemove("camp_official_visits", id);

export const getCampLibrary = (filter) => supaList("camp_library", { filter });
export const createCampLibraryItem = (row) => supaCreate("camp_library", row);
export const updateCampLibraryItem = (id, patch) => supaPatch("camp_library", id, patch);
export const deleteCampLibraryItem = (id) => supaRemove("camp_library", id);

export const getCampLessonsLearned = (filter) => supaList("camp_lessons_learned", { filter });
export const createCampLessonLearned = (row) => supaCreate("camp_lessons_learned", row);
export const updateCampLessonLearned = (id, patch) => supaPatch("camp_lessons_learned", id, patch);
export const deleteCampLessonLearned = (id) => supaRemove("camp_lessons_learned", id);

// ----- Housing -----
export const getHousingResidences = (filter) => supaList("housing_residences", { filter });
export const createHousingResidence = (row) => supaCreate("housing_residences", row);
export const updateHousingResidence = (id, patch) => supaPatch("housing_residences", id, patch);
export const deleteHousingResidence = (id) => supaRemove("housing_residences", id);

export const getHousingWorkers = (filter) => supaList("housing_workers", { filter });
export const createHousingWorker = (row) => supaCreate("housing_workers", row);
export const updateHousingWorker = (id, patch) => supaPatch("housing_workers", id, patch);
export const deleteHousingWorker = (id) => supaRemove("housing_workers", id);

export const getHousingVisits = (filter) => supaList("housing_visits", { filter });
export const createHousingVisit = (row) => supaCreate("housing_visits", row);
export const updateHousingVisit = (id, patch) => supaPatch("housing_visits", id, patch);
export const deleteHousingVisit = (id) => supaRemove("housing_visits", id);

export const getHousingPrograms = (filter) => supaList("housing_programs", { filter });
export const createHousingProgram = (row) => supaCreate("housing_programs", row);
export const updateHousingProgram = (id, patch) => supaPatch("housing_programs", id, patch);
export const deleteHousingProgram = (id) => supaRemove("housing_programs", id);

export const getHousingNewMuslims = (filter) => supaList("housing_new_muslims", { filter });
export const createHousingNewMuslim = (row) => supaCreate("housing_new_muslims", row);
export const updateHousingNewMuslim = (id, patch) => supaPatch("housing_new_muslims", id, patch);
export const deleteHousingNewMuslim = (id) => supaRemove("housing_new_muslims", id);

export const getHousingEducation = (filter) => supaList("housing_education", { filter });
export const createHousingEducation = (row) => supaCreate("housing_education", row);
export const updateHousingEducation = (id, patch) => supaPatch("housing_education", id, patch);
export const deleteHousingEducation = (id) => supaRemove("housing_education", id);

export const getHousingMaterials = (filter) => supaList("housing_materials", { filter });
export const createHousingMaterial = (row) => supaCreate("housing_materials", row);
export const updateHousingMaterial = (id, patch) => supaPatch("housing_materials", id, patch);
export const deleteHousingMaterial = (id) => supaRemove("housing_materials", id);

export const getHousingPreachers = (filter) => supaList("housing_preachers", { filter });
export const createHousingPreacher = (row) => supaCreate("housing_preachers", row);
export const updateHousingPreacher = (id, patch) => supaPatch("housing_preachers", id, patch);
export const deleteHousingPreacher = (id) => supaRemove("housing_preachers", id);

// ----- Requests (additions only — original functions above are untouched) -----
export const getRequestById = (id) => supaGetById("requests", id);
export const updateRequestStatus = (id, status) => supaPatch("requests", id, { status });

// ===== Communities — New Muslims (المسلمون الجدد) CRUD =====
// جدول communities_new_muslims: RLS مُفعّل، سياسة الإدراج with check (true)
// لأن النظام يرسل public.users.id في created_by (ربط auth لم يكتمل بعد).
// نفس نمط دوال الاجتماعات: نرجّع مصفوفة/صفًا ونرمي عند الخطأ.
export async function getCommunityNewMuslims() {
  const { data, error } = await supaList("communities_new_muslims", {
    orderBy: "islam_date",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب المسلمين الجدد: ${error}`);
  return data || [];
}

export async function createCommunityNewMuslim(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("communities_new_muslims", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) {
    throw new Error(`تعذّر تسجيل المسلم الجديد: ${error}`);
  }
  return data;
}

export async function updateCommunityNewMuslim(id, patch) {
  const { data, error } = await supaPatch("communities_new_muslims", id, patch);
  if (error) throw new Error(`تعذّر تحديث بيانات المسلم الجديد: ${error}`);
  return data;
}

export async function deleteCommunityNewMuslim(id) {
  const { error } = await supaRemove("communities_new_muslims", id);
  if (error) throw new Error(`تعذّر حذف المسلم الجديد: ${error}`);
  return { id };
}

// ===== Communities — Preachers (الدعاة) CRUD =====
// جدول communities_preachers: RLS مُفعّل، سياسة الإدراج with check (true)
// لأن النظام يرسل public.users.id في created_by (ربط auth لم يكتمل بعد).
// نفس نمط دوال communities_new_muslims: نرجّع مصفوفة/صفًا ونرمي عند الخطأ.
export async function getCommunityPreachers() {
  const { data, error } = await supaList("communities_preachers", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الدعاة: ${error}`);
  return data || [];
}

export async function createCommunityPreacher(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("communities_preachers", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة الداعية: ${error}`);
  return data;
}

export async function updateCommunityPreacher(id, patch) {
  const { data, error } = await supaPatch("communities_preachers", id, patch);
  if (error) throw new Error(`تعذّر تحديث بيانات الداعية: ${error}`);
  return data;
}

export async function deleteCommunityPreacher(id) {
  const { error } = await supaRemove("communities_preachers", id);
  if (error) throw new Error(`تعذّر حذف الداعية: ${error}`);
  return { id };
}

// ===== Communities — Programs (البرامج) CRUD =====
// جدول communities_programs: نفس نمط الدعاة أعلاه.
export async function getCommunityPrograms() {
  const { data, error } = await supaList("communities_programs", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب البرامج: ${error}`);
  return data || [];
}

export async function createCommunityProgram(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("communities_programs", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة البرنامج: ${error}`);
  return data;
}

export async function updateCommunityProgram(id, patch) {
  const { data, error } = await supaPatch("communities_programs", id, patch);
  if (error) throw new Error(`تعذّر تحديث بيانات البرنامج: ${error}`);
  return data;
}

export async function deleteCommunityProgram(id) {
  const { error } = await supaRemove("communities_programs", id);
  if (error) throw new Error(`تعذّر حذف البرنامج: ${error}`);
  return { id };
}

// ===== Communities — Beneficiaries (المستفيدون) CRUD =====
// جدول communities_beneficiaries: نفس نمط الدعاة أعلاه.
export async function getCommunityBeneficiaries() {
  const { data, error } = await supaList("communities_beneficiaries", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب المستفيدين: ${error}`);
  return data || [];
}

export async function createCommunityBeneficiary(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("communities_beneficiaries", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة المستفيد: ${error}`);
  return data;
}

export async function updateCommunityBeneficiary(id, patch) {
  const { data, error } = await supaPatch("communities_beneficiaries", id, patch);
  if (error) throw new Error(`تعذّر تحديث بيانات المستفيد: ${error}`);
  return data;
}

export async function deleteCommunityBeneficiary(id) {
  const { error } = await supaRemove("communities_beneficiaries", id);
  if (error) throw new Error(`تعذّر حذف المستفيد: ${error}`);
  return { id };
}

// ===== Donors (المتبرعون) CRUD =====
// جدول donors: RLS مُفعّل، سياسة الإدراج with check (true)
// لأن النظام يرسل public.users.id في created_by (ربط auth لم يكتمل بعد).
// نفس نمط دوال communities_new_muslims: نرجّع مصفوفة/صفًا ونرمي عند الخطأ.
export async function getDonors() {
  const { data, error } = await supaList("donors", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب المتبرعين: ${error}`);
  return data || [];
}

export async function createDonor(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("donors", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة المتبرع: ${error}`);
  return data;
}

export async function updateDonor(id, patch) {
  const { data, error } = await supaPatch("donors", id, patch);
  if (error) throw new Error(`تعذّر تحديث بيانات المتبرع: ${error}`);
  return data;
}

export async function deleteDonor(id) {
  const { error } = await supaRemove("donors", id);
  if (error) throw new Error(`تعذّر حذف المتبرع: ${error}`);
  return { id };
}

// ===== الجهات المانحة (grant_donors) — منفصل عن donors (نموذج مختلف) =====
export async function getGrantDonors() {
  const { data, error } = await supaList("grant_donors", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الجهات المانحة: ${error}`);
  return data || [];
}

export async function createGrantDonor(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("grant_donors", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة الجهة المانحة: ${error}`);
  return data;
}

export async function updateGrantDonor(id, patch) {
  const { data, error } = await supaPatch("grant_donors", id, patch);
  if (error) throw new Error(`تعذّر تحديث بيانات الجهة المانحة: ${error}`);
  return data;
}

export async function deleteGrantDonor(id) {
  const { error } = await supaRemove("grant_donors", id);
  if (error) throw new Error(`تعذّر حذف الجهة المانحة: ${error}`);
  return { id };
}

// ===== المنح والمشاريع (grants) =====
export async function getGrants() {
  const { data, error } = await supaList("grants", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب المنح: ${error}`);
  return data || [];
}
export async function createGrant(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("grants", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة المنحة: ${error}`);
  return data;
}
export async function updateGrant(id, patch) {
  const { data, error } = await supaPatch("grants", id, patch);
  if (error) throw new Error(`تعذّر تحديث المنحة: ${error}`);
  return data;
}
export async function deleteGrant(id) {
  const { error } = await supaRemove("grants", id);
  if (error) throw new Error(`تعذّر حذف المنحة: ${error}`);
  return { id };
}

// ===== تقارير المنح (grant_reports) =====
export async function getGrantReports() {
  const { data, error } = await supaList("grant_reports", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب تقارير المنح: ${error}`);
  return data || [];
}
export async function createGrantReport(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("grant_reports", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة التقرير: ${error}`);
  return data;
}
export async function updateGrantReport(id, patch) {
  const { data, error } = await supaPatch("grant_reports", id, patch);
  if (error) throw new Error(`تعذّر تحديث التقرير: ${error}`);
  return data;
}
export async function deleteGrantReport(id) {
  const { error } = await supaRemove("grant_reports", id);
  if (error) throw new Error(`تعذّر حذف التقرير: ${error}`);
  return { id };
}

// ===== Pledges (التعهدات) CRUD =====
// جدول donors_pledges: RLS مُفعّل، سياسة الإدراج with check (true)
// لأن النظام يرسل public.users.id في created_by (ربط auth لم يكتمل بعد).
// نفس نمط دوال donors: نرجّع مصفوفة/صفًا ونرمي عند الخطأ.
// ملاحظة: donor_id يشير إلى public.donors(id)؛ الواجهة تحوّل الاسم -> id عبر الـ dropdown.
export async function getPledges() {
  const { data, error } = await supaList("donors_pledges", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب التعهدات: ${error}`);
  return data || [];
}

export async function createPledge(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("donors_pledges", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة التعهد: ${error}`);
  return data;
}

export async function updatePledge(id, patch) {
  const { data, error } = await supaPatch("donors_pledges", id, patch);
  if (error) throw new Error(`تعذّر تحديث التعهد: ${error}`);
  return data;
}

export async function deletePledge(id) {
  const { error } = await supaRemove("donors_pledges", id);
  if (error) throw new Error(`تعذّر حذف التعهد: ${error}`);
  return { id };
}

// ===== Meetings (Phase 2 — CRUD wrappers على جدول meetings) =====
// RLS مُفعّل مع DEFAULT created_by = auth.uid()، لذلك لا نرسل created_by في الـ INSERT.

export async function getMeetings(module) {
  try {
    const query = `?module=eq.${encodeURIComponent(module)}&select=*&order=meeting_date.desc`;
    const rows = await supaFetch("meetings", { query });
    return Array.isArray(rows) ? rows : rows ? [rows] : [];
  } catch (error) {
    throw new Error(`تعذّر جلب الاجتماعات: ${error?.message || String(error)}`);
  }
}

export async function createMeeting(row) {
  try {
    const payload = { ...row };
    // احذف created_by عشان DEFAULT auth.uid() يشتغل وتعدّي سياسة الإدراج (RLS)
    delete payload.created_by;
    const rows = await supaFetch("meetings", {
      method: "POST",
      body: payload,
      headers: { Prefer: "return=representation" },
    });
    return Array.isArray(rows) ? rows[0] || null : rows;
  } catch (error) {
    throw new Error(`تعذّر إنشاء الاجتماع: ${error?.message || String(error)}`);
  }
}

export async function updateMeeting(id, patch) {
  try {
    const rows = await supaFetch("meetings", {
      method: "PATCH",
      body: patch,
      query: `?id=eq.${encodeURIComponent(id)}`,
    });
    return Array.isArray(rows) ? rows[0] || null : rows;
  } catch (error) {
    throw new Error(`تعذّر تحديث الاجتماع: ${error?.message || String(error)}`);
  }
}

export async function deleteMeeting(id) {
  try {
    await supaFetch("meetings", {
      method: "DELETE",
      query: `?id=eq.${encodeURIComponent(id)}`,
    });
    return { id };
  } catch (error) {
    throw new Error(`تعذّر حذف الاجتماع: ${error?.message || String(error)}`);
  }
}

// ===== Supabase SDK instance (Phase 12c-1 — used by src/supabaseAuth.js only) =====
// إضافة آمنة: لا تستخدمها الدوال القديمة أعلاه. مخصصة لطبقة Auth الحديثة فقط.
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: typeof window !== "undefined" ? window.localStorage : undefined,
  },
});

// ===== Attachments & Storage (المرحلة 2) =====
// دوال التخزين الحقيقي (Supabase Storage) + صفوف جدول attachments.
// نفس نمط supaFetch: نجلب access_token من الجلسة مع fallback آمن للـ anon key.

export const ATTACHMENTS_BUCKET = "attachments";
export const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10MB
export const ALLOWED_ATTACHMENT_EXTS = ["pdf", "png", "jpg", "jpeg", "webp", "docx", "xlsx"];
export const ALLOWED_ATTACHMENT_MIMES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",        // xlsx
];

// امتداد الملف بحروف ascii آمنة فقط (بدون نقطة)
function _fileExt(fileName = "") {
  const raw = (fileName.split(".").pop() || "").toLowerCase();
  return raw.replace(/[^a-z0-9]/g, "");
}

// التحقق من الحجم والنوع — يعيد رسالة خطأ عربية أو null إذا كان الملف صالحًا.
export function validateAttachmentFile(file) {
  if (!file) return "لم يتم اختيار أي ملف.";
  if (file.size > MAX_ATTACHMENT_SIZE) {
    return `حجم الملف يتجاوز الحد المسموح (10 ميجابايت).`;
  }
  const ext = _fileExt(file.name);
  const extOk = ALLOWED_ATTACHMENT_EXTS.includes(ext);
  const mimeOk = !file.type || ALLOWED_ATTACHMENT_MIMES.includes(file.type);
  if (!extOk || !mimeOk) {
    return "نوع الملف غير مسموح. الأنواع المدعومة: PDF، PNG، JPG، JPEG، WEBP، DOCX، XLSX.";
  }
  return null;
}

// توليد مفتاح تخزين ascii-آمن: {module}/{recordId}/{uuid}.{ext}
export function buildAttachmentPath(module, recordId, file) {
  const ext = _fileExt(file?.name || "");
  const uuid = crypto.randomUUID();
  const key = `${module}/${recordId}/${uuid}${ext ? "." + ext : ""}`;
  return key;
}

// جلب access_token للجلسة الحالية مع fallback للـ anon key
async function _storageAccessToken() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || SUPABASE_ANON_KEY;
  } catch (_e) {
    return SUPABASE_ANON_KEY;
  }
}

// رفع ملف خام إلى Storage. path يجب أن يكون مفتاحًا ascii-آمنًا (استخدم buildAttachmentPath).
export async function uploadFile(bucket, path, file) {
  const accessToken = await _storageAccessToken();
  const url = `${SUPABASE_URL}/storage/v1/object/${bucket}/${path}`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": file.type || "application/octet-stream",
        "x-upsert": "false",
      },
      body: file,
    });
    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`(${res.status}) ${detail}`);
    }
    return { bucket, path };
  } catch (error) {
    throw new Error(`تعذّر رفع الملف: ${error?.message || String(error)}`);
  }
}

// إنشاء رابط موقّع للتنزيل. يعيد رابطًا كاملاً صالحًا للفترة المحددة (ثوانٍ).
export async function getSignedUrl(bucket, path, expiresIn = 3600) {
  const accessToken = await _storageAccessToken();
  const url = `${SUPABASE_URL}/storage/v1/object/sign/${bucket}/${path}`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ expiresIn }),
    });
    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`(${res.status}) ${detail}`);
    }
    const data = await res.json();
    const signed = data?.signedURL || data?.signedUrl;
    if (!signed) throw new Error("لم يُرجع الخادم رابطًا موقّعًا.");
    return `${SUPABASE_URL}/storage/v1${signed}`;
  } catch (error) {
    throw new Error(`تعذّر إنشاء رابط التنزيل: ${error?.message || String(error)}`);
  }
}

// حذف ملف من Storage.
export async function deleteFile(bucket, path) {
  const accessToken = await _storageAccessToken();
  const url = `${SUPABASE_URL}/storage/v1/object/${bucket}/${path}`;
  try {
    const res = await fetch(url, {
      method: "DELETE",
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`(${res.status}) ${detail}`);
    }
    return { bucket, path };
  } catch (error) {
    throw new Error(`تعذّر حذف الملف: ${error?.message || String(error)}`);
  }
}

// إنشاء صف في جدول attachments.
// لا نرسل uploaded_by حتى يعمل DEFAULT auth.uid() وتعدّي سياسة الإدراج (RLS).
export async function createAttachment(row) {
  try {
    const payload = { ...row };
    delete payload.uploaded_by;
    const rows = await supaFetch("attachments", {
      method: "POST",
      body: payload,
      headers: { Prefer: "return=representation" },
    });
    return Array.isArray(rows) ? rows[0] || null : rows;
  } catch (error) {
    throw new Error(`تعذّر تسجيل المرفق: ${error?.message || String(error)}`);
  }
}

// جلب مرفقات نقطة معينة (module + record_id).
export async function getAttachments(module, recordId) {
  try {
    const query = `?module=eq.${encodeURIComponent(module)}&record_id=eq.${encodeURIComponent(recordId)}&select=*&order=created_at.desc`;
    const rows = await supaFetch("attachments", { query });
    return Array.isArray(rows) ? rows : rows ? [rows] : [];
  } catch (error) {
    throw new Error(`تعذّر جلب المرفقات: ${error?.message || String(error)}`);
  }
}

// حذف صف مرفق من الجدول (لا يحذف الملف من Storage — يتم ذلك عبر deleteFile).
export async function deleteAttachment(id) {
  try {
    await supaFetch("attachments", {
      method: "DELETE",
      query: `?id=eq.${encodeURIComponent(id)}`,
    });
    return { id };
  } catch (error) {
    throw new Error(`تعذّر حذف المرفق: ${error?.message || String(error)}`);
  }
}

// ===== Transactions (المعاملات) CRUD — مفلترة بالموديول =====
// جدول transactions: RLS مُفعّل، عمود module موحّد لكل الموديولات.
// كل موديول يقرأ معاملاته فقط عبر فلتر module=eq. نفس نمط دوال المسلمين الجدد:
// نرجّع مصفوفة/صفًا ونرمي عند الخطأ.
export async function getTransactions(module, direction = null) {
  const { data, error } = await supaList("transactions", {
    filter: { module, direction },
    orderBy: "txn_date",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب المعاملات: ${error}`);
  return data || [];
}

export async function createTransaction(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("transactions", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) {
    throw new Error(`تعذّر إنشاء المعاملة: ${error}`);
  }
  return data;
}

export async function updateTransaction(id, patch) {
  const { data, error } = await supaPatch("transactions", id, patch);
  if (error) throw new Error(`تعذّر تحديث المعاملة: ${error}`);
  return data;
}

export async function deleteTransaction(id) {
  const { error } = await supaRemove("transactions", id);
  if (error) throw new Error(`تعذّر حذف المعاملة: ${error}`);
  return { id };
}

// ===== Employees (الموظفون) CRUD =====
// جدول employees: RLS مُفعّل، سياسة الإدراج with check (true) (created_by من public.users.id).
// نفس نمط دوال donors: نرجّع مصفوفة/صفًا ونرمي عند الخطأ. لا يُرسل عمود module (جدول مستقل).
export async function getEmployees() {
  const { data, error } = await supaList("employees", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الموظفين: ${error}`);
  return data || [];
}

export async function createEmployee(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("employees", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة الموظف: ${error}`);
  return data;
}

export async function updateEmployee(id, patch) {
  const { data, error } = await supaPatch("employees", id, patch);
  if (error) throw new Error(`تعذّر تحديث بيانات الموظف: ${error}`);
  return data;
}

export async function deleteEmployee(id) {
  const { error } = await supaRemove("employees", id);
  if (error) throw new Error(`تعذّر حذف الموظف: ${error}`);
  return { id };
}

// ===== Leaves (الإجازات) CRUD =====
// جدول leaves: employee_id يشير إلى public.employees(id)؛ الواجهة تحوّل الاسم → id عبر الـ dropdown.
// نفس نمط دوال donors. لا يُرسل عمود module (جدول مستقل).
export async function getLeaves() {
  const { data, error } = await supaList("leaves", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الإجازات: ${error}`);
  return data || [];
}

export async function createLeave(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("leaves", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة الإجازة: ${error}`);
  return data;
}

export async function updateLeave(id, patch) {
  const { data, error } = await supaPatch("leaves", id, patch);
  if (error) throw new Error(`تعذّر تحديث الإجازة: ${error}`);
  return data;
}

export async function deleteLeave(id) {
  const { error } = await supaRemove("leaves", id);
  if (error) throw new Error(`تعذّر حذف الإجازة: ${error}`);
  return { id };
}

// ===== Volunteers (المتطوعون) CRUD =====
// جدول volunteers: RLS مُفعّل، سياسة الإدراج with check (true) (created_by من public.users.id).
// نفس نمط دوال donors/employees: نرجّع مصفوفة/صفًا ونرمي عند الخطأ. لا يُرسل عمود module (جدول مستقل).
export async function getVolunteers() {
  const { data, error } = await supaList("volunteers", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب المتطوعين: ${error}`);
  return data || [];
}

export async function createVolunteer(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("volunteers", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة المتطوع: ${error}`);
  return data;
}

export async function updateVolunteer(id, patch) {
  const { data, error } = await supaPatch("volunteers", id, patch);
  if (error) throw new Error(`تعذّر تحديث بيانات المتطوع: ${error}`);
  return data;
}

export async function deleteVolunteer(id) {
  const { error } = await supaRemove("volunteers", id);
  if (error) throw new Error(`تعذّر حذف المتطوع: ${error}`);
  return { id };
}

// ===== Volunteer Teams (الفرق التطوعية) CRUD =====
// جدول volunteer_teams: نفس نمط donors. لا يُرسل عمود module (جدول مستقل).
export async function getVolunteerTeams() {
  const { data, error } = await supaList("volunteer_teams", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الفرق التطوعية: ${error}`);
  return data || [];
}

export async function createVolunteerTeam(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("volunteer_teams", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إنشاء الفريق: ${error}`);
  return data;
}

export async function updateVolunteerTeam(id, patch) {
  const { data, error } = await supaPatch("volunteer_teams", id, patch);
  if (error) throw new Error(`تعذّر تحديث الفريق: ${error}`);
  return data;
}

export async function deleteVolunteerTeam(id) {
  const { error } = await supaRemove("volunteer_teams", id);
  if (error) throw new Error(`تعذّر حذف الفريق: ${error}`);
  return { id };
}

// ===== Volunteer Opportunities (الفرص التطوعية) CRUD =====
// جدول volunteer_opportunities: نفس نمط donors. لا يُرسل عمود module (جدول مستقل).
export async function getVolunteerOpportunities() {
  const { data, error } = await supaList("volunteer_opportunities", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الفرص التطوعية: ${error}`);
  return data || [];
}

export async function createVolunteerOpportunity(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("volunteer_opportunities", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إنشاء الفرصة: ${error}`);
  return data;
}

export async function updateVolunteerOpportunity(id, patch) {
  const { data, error } = await supaPatch("volunteer_opportunities", id, patch);
  if (error) throw new Error(`تعذّر تحديث الفرصة: ${error}`);
  return data;
}

export async function deleteVolunteerOpportunity(id) {
  const { error } = await supaRemove("volunteer_opportunities", id);
  if (error) throw new Error(`تعذّر حذف الفرصة: ${error}`);
  return { id };
}

// ===== Volunteer Evaluations (تقييمات المتطوعين) CRUD =====
// جدول volunteer_evaluations: volunteer_id يشير إلى public.volunteers(id) ON DELETE CASCADE.
// نفس نمط دوال donors. لا يُرسل عمود module (جدول مستقل).
export async function getVolunteerEvaluations() {
  const { data, error } = await supaList("volunteer_evaluations", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب التقييمات: ${error}`);
  return data || [];
}

export async function createVolunteerEvaluation(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("volunteer_evaluations", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر حفظ التقييم: ${error}`);
  return data;
}

export async function updateVolunteerEvaluation(id, patch) {
  const { data, error } = await supaPatch("volunteer_evaluations", id, patch);
  if (error) throw new Error(`تعذّر تحديث التقييم: ${error}`);
  return data;
}

export async function deleteVolunteerEvaluation(id) {
  const { error } = await supaRemove("volunteer_evaluations", id);
  if (error) throw new Error(`تعذّر حذف التقييم: ${error}`);
  return { id };
}

// ===== Mosques / Dawah (الدعوة — المساجد) CRUD =====
// جداول mosques / mosque_programs / mosque_talks / mosque_science_days:
// RLS مُفعّل، سياسة الإدراج with check (true) (created_by من public.users.id).
// نفس نمط دوال volunteers: نرجّع مصفوفة/صفًا ونرمي عند الخطأ. لا عمود module (جداول مستقلة).
export async function getMosques() {
  const { data, error } = await supaList("mosques", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب المساجد: ${error}`);
  return data || [];
}

export async function createMosque(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("mosques", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة الموقع: ${error}`);
  return data;
}

export async function updateMosque(id, patch) {
  const { data, error } = await supaPatch("mosques", id, patch);
  if (error) throw new Error(`تعذّر تحديث الموقع: ${error}`);
  return data;
}

export async function deleteMosque(id) {
  const { error } = await supaRemove("mosques", id);
  if (error) throw new Error(`تعذّر حذف الموقع: ${error}`);
  return { id };
}

export async function getMosquePrograms() {
  const { data, error } = await supaList("mosque_programs", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب البرامج الدعوية: ${error}`);
  return data || [];
}

export async function createMosqueProgram(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("mosque_programs", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة البرنامج: ${error}`);
  return data;
}

export async function updateMosqueProgram(id, patch) {
  const { data, error } = await supaPatch("mosque_programs", id, patch);
  if (error) throw new Error(`تعذّر تحديث البرنامج: ${error}`);
  return data;
}

export async function deleteMosqueProgram(id) {
  const { error } = await supaRemove("mosque_programs", id);
  if (error) throw new Error(`تعذّر حذف البرنامج: ${error}`);
  return { id };
}

export async function getMosqueTalks() {
  const { data, error } = await supaList("mosque_talks", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الكلمات والمحاضرات: ${error}`);
  return data || [];
}

export async function createMosqueTalk(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("mosque_talks", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة الكلمة/المحاضرة: ${error}`);
  return data;
}

export async function updateMosqueTalk(id, patch) {
  const { data, error } = await supaPatch("mosque_talks", id, patch);
  if (error) throw new Error(`تعذّر تحديث الكلمة/المحاضرة: ${error}`);
  return data;
}

export async function deleteMosqueTalk(id) {
  const { error } = await supaRemove("mosque_talks", id);
  if (error) throw new Error(`تعذّر حذف الكلمة/المحاضرة: ${error}`);
  return { id };
}

export async function getMosqueScienceDays() {
  const { data, error } = await supaList("mosque_science_days", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الأيام العلمية: ${error}`);
  return data || [];
}

export async function createMosqueScienceDay(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("mosque_science_days", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة اليوم العلمي: ${error}`);
  return data;
}

export async function updateMosqueScienceDay(id, patch) {
  const { data, error } = await supaPatch("mosque_science_days", id, patch);
  if (error) throw new Error(`تعذّر تحديث اليوم العلمي: ${error}`);
  return data;
}

export async function deleteMosqueScienceDay(id) {
  const { error } = await supaRemove("mosque_science_days", id);
  if (error) throw new Error(`تعذّر حذف اليوم العلمي: ${error}`);
  return { id };
}

// ===== Women (القسم النسائي) CRUD =====
// جداول women_programs / women_lessons / women_forums / women_beneficiaries /
// women_new_muslims / women_preachers / women_content:
// RLS مُفعّل، سياسة الإدراج with check (true) (created_by من public.users.id).
// نفس نمط دوال volunteers: نرجّع مصفوفة/صفًا ونرمي عند الخطأ. لا عمود module (جداول مستقلة).
export async function getWomenPrograms() {
  const { data, error } = await supaList("women_programs", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب البرامج النسائية: ${error}`);
  return data || [];
}

export async function createWomenProgram(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("women_programs", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة البرنامج: ${error}`);
  return data;
}

export async function updateWomenProgram(id, patch) {
  const { data, error } = await supaPatch("women_programs", id, patch);
  if (error) throw new Error(`تعذّر تحديث البرنامج: ${error}`);
  return data;
}

export async function deleteWomenProgram(id) {
  const { error } = await supaRemove("women_programs", id);
  if (error) throw new Error(`تعذّر حذف البرنامج: ${error}`);
  return { id };
}

export async function getWomenLessons() {
  const { data, error } = await supaList("women_lessons", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الدروس والمحاضرات: ${error}`);
  return data || [];
}

export async function createWomenLesson(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("women_lessons", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة الدرس/المحاضرة: ${error}`);
  return data;
}

export async function updateWomenLesson(id, patch) {
  const { data, error } = await supaPatch("women_lessons", id, patch);
  if (error) throw new Error(`تعذّر تحديث الدرس/المحاضرة: ${error}`);
  return data;
}

export async function deleteWomenLesson(id) {
  const { error } = await supaRemove("women_lessons", id);
  if (error) throw new Error(`تعذّر حذف الدرس/المحاضرة: ${error}`);
  return { id };
}

export async function getWomenForums() {
  const { data, error } = await supaList("women_forums", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الملتقيات: ${error}`);
  return data || [];
}

export async function createWomenForum(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("women_forums", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة الملتقى: ${error}`);
  return data;
}

export async function updateWomenForum(id, patch) {
  const { data, error } = await supaPatch("women_forums", id, patch);
  if (error) throw new Error(`تعذّر تحديث الملتقى: ${error}`);
  return data;
}

export async function deleteWomenForum(id) {
  const { error } = await supaRemove("women_forums", id);
  if (error) throw new Error(`تعذّر حذف الملتقى: ${error}`);
  return { id };
}

export async function getWomenBeneficiaries() {
  const { data, error } = await supaList("women_beneficiaries", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب المستفيدات: ${error}`);
  return data || [];
}

export async function createWomenBeneficiary(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("women_beneficiaries", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة المستفيدة: ${error}`);
  return data;
}

export async function updateWomenBeneficiary(id, patch) {
  const { data, error } = await supaPatch("women_beneficiaries", id, patch);
  if (error) throw new Error(`تعذّر تحديث المستفيدة: ${error}`);
  return data;
}

export async function deleteWomenBeneficiary(id) {
  const { error } = await supaRemove("women_beneficiaries", id);
  if (error) throw new Error(`تعذّر حذف المستفيدة: ${error}`);
  return { id };
}

export async function getWomenNewMuslims() {
  const { data, error } = await supaList("women_new_muslims", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب المسلمات الجديدات: ${error}`);
  return data || [];
}

export async function createWomenNewMuslim(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("women_new_muslims", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر تسجيل المسلمة الجديدة: ${error}`);
  return data;
}

export async function updateWomenNewMuslim(id, patch) {
  const { data, error } = await supaPatch("women_new_muslims", id, patch);
  if (error) throw new Error(`تعذّر تحديث بيانات المسلمة الجديدة: ${error}`);
  return data;
}

export async function deleteWomenNewMuslim(id) {
  const { error } = await supaRemove("women_new_muslims", id);
  if (error) throw new Error(`تعذّر حذف المسلمة الجديدة: ${error}`);
  return { id };
}

export async function getWomenPreachers() {
  const { data, error } = await supaList("women_preachers", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الداعيات: ${error}`);
  return data || [];
}

export async function createWomenPreacher(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("women_preachers", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة الداعية: ${error}`);
  return data;
}

export async function updateWomenPreacher(id, patch) {
  const { data, error } = await supaPatch("women_preachers", id, patch);
  if (error) throw new Error(`تعذّر تحديث الداعية: ${error}`);
  return data;
}

export async function deleteWomenPreacher(id) {
  const { error } = await supaRemove("women_preachers", id);
  if (error) throw new Error(`تعذّر حذف الداعية: ${error}`);
  return { id };
}

export async function getWomenContent() {
  const { data, error } = await supaList("women_content", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب المحتوى النسائي: ${error}`);
  return data || [];
}

export async function createWomenContentItem(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("women_content", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة المحتوى: ${error}`);
  return data;
}

export async function updateWomenContentItem(id, patch) {
  const { data, error } = await supaPatch("women_content", id, patch);
  if (error) throw new Error(`تعذّر تحديث المحتوى: ${error}`);
  return data;
}

export async function deleteWomenContentItem(id) {
  const { error } = await supaRemove("women_content", id);
  if (error) throw new Error(`تعذّر حذف المحتوى: ${error}`);
  return { id };
}

// ===== Events (الأحداث والأنشطة) CRUD =====
// جداول events / campaigns — انظر scripts/events-tables.sql
export async function getEvents() {
  const { data, error } = await supaList("events", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الأحداث: ${error}`);
  return data || [];
}

export async function createEvent(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("events", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة الحدث: ${error}`);
  return data;
}

export async function updateEvent(id, patch) {
  const { data, error } = await supaPatch("events", id, patch);
  if (error) throw new Error(`تعذّر تحديث الحدث: ${error}`);
  return data;
}

export async function deleteEvent(id) {
  const { error } = await supaRemove("events", id);
  if (error) throw new Error(`تعذّر حذف الحدث: ${error}`);
  return { id };
}

export async function getCampaigns() {
  const { data, error } = await supaList("campaigns", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الحملات: ${error}`);
  return data || [];
}

export async function createCampaign(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("campaigns", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة الحملة: ${error}`);
  return data;
}

export async function updateCampaign(id, patch) {
  const { data, error } = await supaPatch("campaigns", id, patch);
  if (error) throw new Error(`تعذّر تحديث الحملة: ${error}`);
  return data;
}

export async function deleteCampaign(id) {
  const { error } = await supaRemove("campaigns", id);
  if (error) throw new Error(`تعذّر حذف الحملة: ${error}`);
  return { id };
}

// ===== Fundraising (تنمية الموارد) CRUD =====
// جداول fundraising_projects / fundraising_partners — انظر scripts/fundraising-tables.sql
// نفس نمط دوال events: نرجّع مصفوفة/صفًا ونرمي عند الخطأ. لا عمود module (جداول مستقلة).
export async function getFundraisingProjects() {
  const { data, error } = await supaList("fundraising_projects", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب المشاريع: ${error}`);
  return data || [];
}

export async function createFundraisingProject(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("fundraising_projects", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إنشاء المشروع: ${error}`);
  return data;
}

export async function updateFundraisingProject(id, patch) {
  const { data, error } = await supaPatch("fundraising_projects", id, patch);
  if (error) throw new Error(`تعذّر تحديث المشروع: ${error}`);
  return data;
}

export async function deleteFundraisingProject(id) {
  const { error } = await supaRemove("fundraising_projects", id);
  if (error) throw new Error(`تعذّر حذف المشروع: ${error}`);
  return { id };
}

export async function getFundraisingPartners() {
  const { data, error } = await supaList("fundraising_partners", {
    orderBy: "created_at",
    ascending: false,
  });
  if (error) throw new Error(`تعذّر جلب الشراكات: ${error}`);
  return data || [];
}

export async function createFundraisingPartner(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("fundraising_partners", {
    ...row,
    created_by: row?.created_by || user?.id || null,
  });
  if (error) throw new Error(`تعذّر إضافة الشراكة: ${error}`);
  return data;
}

export async function updateFundraisingPartner(id, patch) {
  const { data, error } = await supaPatch("fundraising_partners", id, patch);
  if (error) throw new Error(`تعذّر تحديث الشراكة: ${error}`);
  return data;
}

export async function deleteFundraisingPartner(id) {
  const { error } = await supaRemove("fundraising_partners", id);
  if (error) throw new Error(`تعذّر حذف الشراكة: ${error}`);
  return { id };
}

// ===== Board (مجلس الإدارة) CRUD =====
// جداول board_members / board_decisions / board_committees — انظر scripts/board-tables.sql
// اجتماعات المجلس تستخدم getMeetings/createMeeting المشتركة (module="board").
export async function getBoardMembers() {
  const { data, error } = await supaList("board_members", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب الأعضاء: ${error}`);
  return data || [];
}

export async function createBoardMember(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("board_members", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة العضو: ${error}`);
  return data;
}

export async function getBoardDecisions() {
  const { data, error } = await supaList("board_decisions", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب القرارات: ${error}`);
  return data || [];
}

export async function createBoardDecision(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("board_decisions", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر حفظ القرار: ${error}`);
  return data;
}

export async function getBoardCommittees() {
  const { data, error } = await supaList("board_committees", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب اللجان: ${error}`);
  return data || [];
}

export async function createBoardCommittee(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("board_committees", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إنشاء اللجنة: ${error}`);
  return data;
}

// ===== Communications (الاتصال المؤسسي) CRUD =====
// جداول comms_* — انظر scripts/comms-tables.sql
export async function getCommsNews() {
  const { data, error } = await supaList("comms_news", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب الأخبار: ${error}`);
  return data || [];
}

export async function createCommsNews(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("comms_news", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر حفظ الخبر: ${error}`);
  return data;
}

export async function getCommsPublications() {
  const { data, error } = await supaList("comms_publications", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب المطبوعات: ${error}`);
  return data || [];
}

export async function createCommsPublication(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("comms_publications", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة المطبوعة: ${error}`);
  return data;
}

export async function getCommsMedia() {
  const { data, error } = await supaList("comms_media", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب التغطيات: ${error}`);
  return data || [];
}

export async function createCommsMedia(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("comms_media", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر حفظ التغطية: ${error}`);
  return data;
}

export async function getCommsSuppliers() {
  const { data, error } = await supaList("comms_suppliers", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب الموردين: ${error}`);
  return data || [];
}

export async function createCommsSupplier(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("comms_suppliers", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة المورّد: ${error}`);
  return data;
}

export async function getCommsSocialAccounts() {
  const { data, error } = await supaList("comms_social_accounts", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب الحسابات: ${error}`);
  return data || [];
}

export async function createCommsSocialAccount(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("comms_social_accounts", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة الحساب: ${error}`);
  return data;
}

export async function updateCommsSocialAccount(id, patch) {
  const { data, error } = await supaPatch("comms_social_accounts", id, patch);
  if (error) throw new Error(`تعذّر تحديث الحساب: ${error}`);
  return data;
}

export async function getCommsAchievements() {
  const { data, error } = await supaList("comms_achievements", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب الإنجازات: ${error}`);
  return data || [];
}

export async function createCommsAchievement(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("comms_achievements", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة الإنجاز: ${error}`);
  return data;
}

// ===== Governance (الحوكمة) CRUD =====
// جداول gov_* — انظر scripts/governance-tables.sql. القرارات تستخدم board_decisions.
export async function getGovPolicies() {
  const { data, error } = await supaList("gov_policies", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب السياسات: ${error}`);
  return data || [];
}

export async function createGovPolicy(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("gov_policies", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة السياسة: ${error}`);
  return data;
}

export async function getGovRisks() {
  const { data, error } = await supaList("gov_risks", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب المخاطر: ${error}`);
  return data || [];
}

export async function createGovRisk(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("gov_risks", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة الخطر: ${error}`);
  return data;
}

export async function getGovAudits() {
  const { data, error } = await supaList("gov_audits", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب عمليات التدقيق: ${error}`);
  return data || [];
}

export async function createGovAudit(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("gov_audits", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إنشاء التدقيق: ${error}`);
  return data;
}

export async function getGovConflicts() {
  const { data, error } = await supaList("gov_conflicts", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب الإفصاحات: ${error}`);
  return data || [];
}

export async function createGovConflict(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("gov_conflicts", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر تسجيل الإفصاح: ${error}`);
  return data;
}

export async function getGovLicenses() {
  const { data, error } = await supaList("gov_licenses", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب التراخيص: ${error}`);
  return data || [];
}

export async function createGovLicense(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("gov_licenses", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر حفظ الترخيص: ${error}`);
  return data;
}

export async function getGovFinStatements() {
  const { data, error } = await supaList("gov_fin_statements", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب القوائم المالية: ${error}`);
  return data || [];
}

export async function createGovFinStatement(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("gov_fin_statements", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة القائمة المالية: ${error}`);
  return data;
}

export async function getGovProjects() {
  const { data, error } = await supaList("gov_projects", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب المشروعات: ${error}`);
  return data || [];
}

export async function createGovProject(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("gov_projects", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة المشروع: ${error}`);
  return data;
}

/* ─── Governance: Strategic Goals ─── */
export async function getGovGoals() {
  const { data, error } = await supaList("gov_goals", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب الأهداف الاستراتيجية: ${error}`);
  return data || [];
}
export async function createGovGoal(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("gov_goals", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة الهدف: ${error}`);
  return data;
}
export async function updateGovGoal(id, patch) {
  const { data, error } = await supaPatch("gov_goals", id, patch);
  if (error) throw new Error(`تعذّر تحديث الهدف: ${error}`);
  return data;
}
export async function deleteGovGoal(id) {
  const { error } = await supaRemove("gov_goals", id);
  if (error) throw new Error(`تعذّر حذف الهدف: ${error}`);
  return true;
}

/* ─── Governance: Satisfaction Surveys ─── */
export async function getGovSurveys() {
  const { data, error } = await supaList("gov_surveys", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب الاستبيانات: ${error}`);
  return data || [];
}
export async function createGovSurvey(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("gov_surveys", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة الاستبيان: ${error}`);
  return data;
}
export async function updateGovSurvey(id, patch) {
  const { data, error } = await supaPatch("gov_surveys", id, patch);
  if (error) throw new Error(`تعذّر تحديث الاستبيان: ${error}`);
  return data;
}
export async function deleteGovSurvey(id) {
  const { error } = await supaRemove("gov_surveys", id);
  if (error) throw new Error(`تعذّر حذف الاستبيان: ${error}`);
  return true;
}

/* ─── Governance: Compliance Requirements (متطلبات الامتثال) ─── */
export async function getGovCompliance() {
  const { data, error } = await supaList("gov_compliance", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب متطلبات الامتثال: ${error}`);
  return data || [];
}
export async function createGovCompliance(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("gov_compliance", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة متطلب الامتثال: ${error}`);
  return data;
}
export async function updateGovCompliance(id, patch) {
  const { data, error } = await supaPatch("gov_compliance", id, patch);
  if (error) throw new Error(`تعذّر تحديث متطلب الامتثال: ${error}`);
  return data;
}
export async function deleteGovCompliance(id) {
  const { error } = await supaRemove("gov_compliance", id);
  if (error) throw new Error(`تعذّر حذف متطلب الامتثال: ${error}`);
  return true;
}

/* ─── HR: Allowances ─── */
export async function getAllowances() {
  const { data, error } = await supaList("hr_allowances", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب البدلات: ${error}`);
  return data || [];
}
export async function createAllowance(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("hr_allowances", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة البدل: ${error}`);
  return data;
}
export async function updateAllowance(id, patch) {
  const { data, error } = await supaPatch("hr_allowances", id, patch);
  if (error) throw new Error(`تعذّر تحديث البدل: ${error}`);
  return data;
}
export async function deleteAllowance(id) {
  const { error } = await supaRemove("hr_allowances", id);
  if (error) throw new Error(`تعذّر حذف البدل: ${error}`);
  return true;
}

/* ─── Finance: Budget Items ─── */
export async function getBudgets() {
  const { data, error } = await supaList("budget_items", { orderBy: "created_at", ascending: false });
  if (error) throw new Error(`تعذّر جلب بنود الميزانية: ${error}`);
  return data || [];
}
export async function createBudget(row) {
  const user = getCurrentUser();
  const { data, error } = await supaCreate("budget_items", { ...row, created_by: row?.created_by || user?.id || null });
  if (error) throw new Error(`تعذّر إضافة بند الميزانية: ${error}`);
  return data;
}
export async function updateBudget(id, patch) {
  const { data, error } = await supaPatch("budget_items", id, patch);
  if (error) throw new Error(`تعذّر تحديث بند الميزانية: ${error}`);
  return data;
}
export async function deleteBudget(id) {
  const { error } = await supaRemove("budget_items", id);
  if (error) throw new Error(`تعذّر حذف بند الميزانية: ${error}`);
  return true;
}
